import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, DollarSign, RefreshCw } from "lucide-react";
import api from "../../lib/api";

const DAYS_OPTIONS = [7, 30, 90];

const PURPOSE_LABEL = {
  agent_turn: "Chat",
  doc_extraction: "Extraction",
  screening: "Screening",
  standup: "Standup",
  other: "Other",
};

const PURPOSE_COLOR = {
  agent_turn: "#00E5FF",
  doc_extraction: "#7CE7B7",
  screening: "#F2A65A",
  standup: "#B97AFF",
  other: "#7B7E89",
};

function fmtUsd(v) { return v === 0 ? "$0.00" : `$${(v || 0).toFixed(4)}`; }
function fmtTokens(t) {
  if (t == null) return "0";
  if (t > 1_000_000) return `${(t / 1_000_000).toFixed(2)}M`;
  if (t > 1_000) return `${(t / 1_000).toFixed(1)}k`;
  return String(t);
}


export default function AdminNereaUsageMerchant() {
  const { merchantId } = useParams();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await api.get(
          `/admin/nerea/usage/merchants/${merchantId}?days=${days}&limit=500`,
        );
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Failed to load.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [merchantId, days]);

  const purposeRows = useMemo(() => {
    if (!data?.by_purpose) return [];
    return [...data.by_purpose].sort((a, b) => b.spend_usd - a.spend_usd);
  }, [data]);

  return (
    <div className="max-w-5xl">
      <Link
        to="/admin/nerea/usage"
        data-testid="usage-merchant-back"
        className="inline-flex items-center gap-1 text-[12px] text-zinc-400 hover:text-[#00E5FF] transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to usage
      </Link>

      <div className="mt-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zinc-500">
        <DollarSign className="h-3 w-3 text-[#00E5FF]" /> Nerea · merchant detail
      </div>
      <h1 className="font-display mt-2 text-2xl sm:text-3xl font-semibold tracking-tighter chrome-text">
        {data?.merchant_label?.legal_name || merchantId}
      </h1>
      <div className="mt-1 flex items-center gap-3 text-[12px] text-zinc-500 font-mono">
        <span data-testid="usage-merchant-id">{merchantId}</span>
        {data?.merchant_label?.ref && <span>· {data.merchant_label.ref}</span>}
        {data?.merchant_label?.status && (
          <span className="px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-zinc-300">
            {data.merchant_label.status}
          </span>
        )}
      </div>

      <div className="mt-6 flex items-center gap-2">
        {DAYS_OPTIONS.map((d) => (
          <button
            key={d}
            data-testid={`usage-merchant-window-${d}`}
            onClick={() => setDays(d)}
            className={`text-[12px] px-3 py-1.5 rounded-lg border transition-colors ${
              days === d
                ? "bg-[#00E5FF]/10 border-[#00E5FF]/30 text-[#00E5FF]"
                : "bg-white/[0.02] border-white/[0.08] text-zinc-400 hover:bg-white/[0.05]"
            }`}
          >
            {d} days
          </button>
        ))}
      </div>

      {err && (
        <div data-testid="usage-merchant-error" className="mt-4 rounded-lg border border-rose-400/30 bg-rose-500/[0.05] px-3 py-2 text-[12.5px] text-rose-200">
          {err}
        </div>
      )}

      {loading ? (
        <div className="mt-6 inline-flex items-center gap-2 text-zinc-500 text-[12px]">
          <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* Totals */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="usage-merchant-totals">
            <Kpi label="Spend" value={fmtUsd(data?.totals?.spend_usd || 0)} testId="kpi-merchant-spend" />
            <Kpi label="Calls" value={(data?.totals?.calls || 0).toLocaleString()} testId="kpi-merchant-calls" />
            <Kpi label="Tokens in" value={fmtTokens(data?.totals?.tokens_in)} testId="kpi-merchant-ti" />
            <Kpi label="Tokens out" value={fmtTokens(data?.totals?.tokens_out)} testId="kpi-merchant-to" />
          </div>

          {/* Per-purpose */}
          {purposeRows.length > 0 && (
            <div className="mt-7">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-2">— By purpose</div>
              <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="text-zinc-500 text-left text-[10.5px] uppercase tracking-[0.18em]">
                      <th className="px-4 py-2.5">Purpose</th>
                      <th className="px-4 py-2.5 text-right">Spend</th>
                      <th className="px-4 py-2.5 text-right">Tokens</th>
                      <th className="px-4 py-2.5 text-right">Calls</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purposeRows.map((r) => (
                      <tr key={r.purpose} className="border-t border-white/[0.05] text-zinc-300">
                        <td className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full" style={{ background: PURPOSE_COLOR[r.purpose] || "#7B7E89" }} />
                            {PURPOSE_LABEL[r.purpose] || r.purpose}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono">{fmtUsd(r.spend_usd)}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-zinc-400">{fmtTokens(r.tokens)}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-zinc-400">{(r.calls || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* All calls */}
          <div className="mt-7">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-2">— All calls (most recent first)</div>
            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden">
              <table className="w-full text-[12.5px]" data-testid="usage-merchant-calls-table">
                <thead>
                  <tr className="text-zinc-500 text-left text-[10.5px] uppercase tracking-[0.18em]">
                    <th className="px-3 py-2.5">When</th>
                    <th className="px-3 py-2.5">Purpose</th>
                    <th className="px-3 py-2.5">Channel</th>
                    <th className="px-3 py-2.5">Model</th>
                    <th className="px-3 py-2.5 text-right">Tokens (in/out)</th>
                    <th className="px-3 py-2.5 text-right">Cost</th>
                    <th className="px-3 py-2.5">Request id</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.calls || []).length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-zinc-500" data-testid="usage-merchant-calls-empty">
                        No calls in this window.
                      </td>
                    </tr>
                  )}
                  {(data?.calls || []).map((c) => (
                    <tr key={c.usage_id} className="border-t border-white/[0.05] text-zinc-300">
                      <td className="px-3 py-2 font-mono text-[11.5px] text-zinc-400">
                        {(c.created_at || "").slice(0, 19).replace("T", " ")}
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full" style={{ background: PURPOSE_COLOR[c.purpose] || "#7B7E89" }} />
                          {PURPOSE_LABEL[c.purpose] || c.purpose || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11.5px]">{c.channel}</td>
                      <td className="px-3 py-2 font-mono text-[11.5px] text-zinc-400">{c.model}</td>
                      <td className="px-3 py-2 text-right font-mono text-[11.5px] text-zinc-400">
                        {fmtTokens(c.tokens_in)} / {fmtTokens(c.tokens_out)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{fmtUsd(c.estimated_cost_usd)}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-500 truncate max-w-[160px]">
                        {c.request_id}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


function Kpi({ label, value, testId }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4" data-testid={testId}>
      <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="mt-1 font-display text-xl text-white">{value}</div>
    </div>
  );
}
